import { useEffect, useState } from 'react';
import {
  ShoppingBasket, Package, HardHat, PlusCircle, ScanLine, Loader2, Home,
} from 'lucide-react';
import { api } from '../api';

// LA PANTALLA DE INICIO DE UNA CASA (Bloque L, fase L-3).
//
// POR QUE NO VALE EL TABLERO DE EMPRESA. Aquel contesta «¿cómo va el
// negocio?»: valor del inventario, capital inmovilizado, rotación,
// márgenes. Son las preguntas de quien vende. Quien inventaría su casa
// tiene otras dos, y solo dos:
//
//     ¿qué me falta?        ¿dónde está lo que tengo?
//
// Enseñarle «capital inmovilizado» a alguien que apuntó su taladro no es
// darle de más: es hacerle creer que se equivocó de aplicación.
//
// NO HAY GRÁFICAS. Una casa con doce cosas no tiene tendencias que
// mostrar, y una gráfica de doce puntos es un adorno que ocupa el sitio de
// lo único que importa: qué hay que comprar.

function Tarjeta({ icono: Icono, titulo, valor, pie, onClick, tono = 'normal' }) {
  const colores = tono === 'atencion'
    ? 'border-amber-300 bg-amber-50 text-amber-900'
    : 'border-gray-200 bg-white text-gray-900';
  const Elemento = onClick ? 'button' : 'div';
  return (
    <Elemento
      {...(onClick ? { type: 'button', onClick } : {})}
      className={`border rounded-xl p-4 text-left w-full ${colores}`}
    >
      <div className="flex items-center gap-2">
        <Icono className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="text-sm font-medium">{titulo}</span>
      </div>
      <div className="text-3xl font-semibold tabular-nums mt-1">{valor}</div>
      {pie && <div className="text-xs mt-0.5 opacity-80">{pie}</div>}
    </Elemento>
  );
}

export default function Casa({ irA }) {
  const [datos, setDatos] = useState(null);

  useEffect(() => {
    let vivo = true;
    Promise.all([
      api.get('/lista-compras').catch(() => []),
      api.get('/productos?tipo_inventario=VENTA&limite=200').catch(() => []),
      api.get('/productos?tipo_inventario=ACTIVO&limite=200').catch(() => []),
    ]).then(([lista, consumibles, pertenencias]) => {
      if (!vivo) return;
      setDatos({
        faltan: lista.filter((i) => !i.comprado).length,
        consumibles: consumibles.length,
        pertenencias: pertenencias.length,
        // Lo que se está acabando, para poder nombrarlo en vez de contarlo.
        bajos: consumibles.filter((p) => Number(p.stock_minimo || 0) > 0
          && Number(p.existencia_total || 0) < Number(p.stock_minimo)).slice(0, 3),
      });
    });
    return () => { vivo = false; };
  }, []);

  if (!datos) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Cargando…
      </div>
    );
  }

  const vacia = datos.consumibles === 0 && datos.pertenencias === 0;

  // ESTADO VACIO CON UNA SOLA SALIDA. Quien acaba de crear su cuenta no
  // necesita un resumen de cero: necesita saber por dónde se empieza, y
  // que solo haya un botón es lo que hace que no se dude.
  if (vacia) {
    return (
      <div className="max-w-md mx-auto text-center space-y-4 py-8">
        <div className="inline-flex bg-[var(--color-marca-suave)] text-[var(--color-marca)]
                        rounded-2xl p-4">
          <Home className="h-8 w-8" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Tu casa está vacía</h2>
          <p className="text-sm text-gray-600 mt-1">
            Empieza por una cosa cualquiera: el café, el taladro, lo que tengas
            más a mano. Te tomará menos de un minuto.
          </p>
        </div>
        <button type="button" onClick={() => irA('registrar', {})}
                className="w-full bg-[var(--color-marca)] text-white font-semibold
                           rounded-xl py-4 inline-flex items-center justify-center gap-2">
          <PlusCircle className="h-5 w-5" aria-hidden="true" /> Registrar la primera
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-xl font-semibold text-gray-900">Mi casa</h2>
        <p className="text-sm text-gray-600 mt-0.5">
          Qué te falta y qué tienes. Nada más.
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* LO QUE FALTA VA PRIMERO Y ES LO UNICO QUE PUEDE PONERSE EN
            AMBAR: es la unica cosa de esta pantalla sobre la que hay algo
            que hacer hoy. */}
        <Tarjeta
          icono={ShoppingBasket}
          titulo="Por comprar"
          valor={datos.faltan}
          tono={datos.faltan > 0 ? 'atencion' : 'normal'}
          pie={datos.faltan > 0 ? 'Toca para ver la lista' : 'No falta nada'}
          onClick={() => irA('lista-compras', {})}
        />
        <Tarjeta icono={Package} titulo="Consumibles" valor={datos.consumibles}
                 pie="Cosas que se acaban" onClick={() => irA('productos', {})} />
        <Tarjeta icono={HardHat} titulo="Pertenencias" valor={datos.pertenencias}
                 pie="Cosas que se quedan" onClick={() => irA('activos', {})} />
      </div>

      {(datos.bajos || []).length > 0 && (
        <section className="space-y-2">
          <h3 className="text-xs font-bold text-gray-500 uppercase">Se está acabando</h3>
          {(datos.bajos || []).map((p) => (
            <div key={p.sku}
                 className="bg-white border border-amber-200 rounded-xl p-3 flex
                            items-center justify-between gap-3">
              <span className="text-sm text-gray-900">{p.nombre}</span>
              <span className="text-sm tabular-nums text-amber-800">
                quedan {Number(p.existencia_total || 0)}
              </span>
            </div>
          ))}
        </section>
      )}

      {/* LAS DOS COSAS QUE SE HACEN A DIARIO, a un toque y con texto. */}
      <div className="grid grid-cols-2 gap-3">
        <button type="button" onClick={() => irA('registrar', {})}
                className="border border-gray-200 bg-white rounded-xl py-4 font-semibold
                           text-gray-800 inline-flex items-center justify-center gap-2">
          <PlusCircle className="h-5 w-5" aria-hidden="true" /> Registrar algo
        </button>
        <button type="button" onClick={() => irA('escanear', {})}
                className="border border-gray-200 bg-white rounded-xl py-4 font-semibold
                           text-gray-800 inline-flex items-center justify-center gap-2">
          <ScanLine className="h-5 w-5" aria-hidden="true" /> Escanear
        </button>
      </div>
    </div>
  );
}
